/* ============================================================
   SAM-1167 — unsaved-changes notice in the business page header (approved option A,
   canvas admin v156), replacing the sticky `.bz-page-savebar` banner.

   The Settings forms (BusinessDetailPanel in its 'page' variant, BusinessCalendarEditor)
   own the form state; the header belongs to BusinessPage. A form publishes its save state
   with useHeaderSave() and BusinessPage draws <HeaderSaveStatus> to the right of the tabs:
     ● Unsaved changes · Cancel · Save changes      (only while there are changes)
     Saving…                                         (while the request runs)
     ● Could not save: <why> · Cancel · Try again    (the changes stay in the form)
   ============================================================ */
import React, { createContext, useContext, useEffect, useRef } from 'react';
import { Save } from 'lucide-react';

export interface HeaderSaveState {
  /** Show the notice: there are unsaved changes, or a new record not yet created. */
  visible: boolean;
  /** 'edit' = «Unsaved changes · Cancel · Save changes»; 'create' = «New business · Create business», no Cancel. */
  mode: 'edit' | 'create';
  saving: boolean;
  /** A failed save: this line shows in red with «Try again»; the form keeps its changes. */
  error?: string | null;
  /** The form cannot be saved yet (a required field is empty). */
  saveDisabled?: boolean;
  /** Text of the save button when the form is a create («Create business»). */
  createLabel?: string;
}

export interface HeaderSaveHandlers {
  onSave: () => void;
  onCancel: () => void;
}

export interface PublishedHeaderSave extends HeaderSaveHandlers {
  state: HeaderSaveState;
}

interface HeaderSaveContextValue {
  publish: (value: PublishedHeaderSave | null) => void;
}

export const HeaderSaveContext = createContext<HeaderSaveContextValue | null>(null);

/**
 * Publishes a form's save state to the page header. Outside a BusinessPage (no provider) it does
 * nothing. The handlers are read through a ref, so only a change of `state` republishes.
 */
export function useHeaderSave(state: HeaderSaveState, handlers: HeaderSaveHandlers) {
  const ctx = useContext(HeaderSaveContext);
  const ref = useRef(handlers);
  ref.current = handlers;
  const key = JSON.stringify(state);

  useEffect(() => {
    ctx?.publish({ state: JSON.parse(key), onSave: () => ref.current.onSave(), onCancel: () => ref.current.onCancel() });
  }, [ctx, key]);

  useEffect(() => () => ctx?.publish(null), [ctx]);

  return Boolean(ctx);
}

const dot = <span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor', flexShrink: 0 }} />;

export const HeaderSaveStatus: React.FC<{ value: PublishedHeaderSave | null }> = ({ value }) => {
  if (!value?.state.visible) return null;
  const { state, onSave, onCancel } = value;
  const failed = !!state.error && !state.saving;
  const color = failed ? 'var(--bad-fg, var(--danger, #c0392b))' : 'var(--warn-fg)';

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="header-save-status"
      className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 md:px-0 py-2 md:py-0"
    >
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color }}>
        {state.saving ? null : dot}
        {state.saving
          ? 'Saving…'
          : failed
            ? state.error
            : state.mode === 'create' ? 'New business' : 'Unsaved changes'}
      </span>
      {state.mode === 'edit' && (
        <button type="button" className="bz-secondary-btn" onClick={onCancel} disabled={state.saving}>
          Cancel
        </button>
      )}
      <button type="button" className="bz-primary-btn" onClick={onSave} disabled={state.saving || (!!state.saveDisabled && !failed)}>
        <Save size={14} />
        {failed ? 'Try again' : state.mode === 'create' ? state.createLabel || 'Create' : 'Save changes'}
      </button>
    </div>
  );
};

/** The line of a failed save, as in the approved mock (UnsavedAStates). */
export const SAVE_FAILED_TEXT = 'Couldn’t save. Check your connection and try again.';
/** A save the server refused for the rows of a form: the rows say which. */
export const SAVE_FAILED_ROWS_TEXT = 'Couldn’t save. Check the highlighted rows and try again.';
